import { useCallback, useEffect } from 'react';
import type {
  oneReferenceUtilitiesReturn,
  ReferenceMinimal,
  TransformedFile,
} from './useReferenceFilesTypes';
import { useFetchAndTransformFilesInApi } from './useFetchAndTransformFilesInApi';
import { useFilesForReference } from 'pages/StudyList/components/StudyReferenceFilesProvider';

type useOneReferenceUtilitiesParams = {
  reviewId: string;
  reference: ReferenceMinimal;
  referenceUrl: string;
  shouldForceRefresh: boolean;
};

export function useOneReferenceUtilities({
  reviewId,
  reference,
  referenceUrl,
  shouldForceRefresh = false,
}: useOneReferenceUtilitiesParams): oneReferenceUtilitiesReturn {
  const { id: referenceId } = reference;

  const { files, refresh } = useFilesForReference(referenceId);

  const {
    deleteFile: deleteFileFromApi,
    makeFilePrimary: makeFilePrimaryInApi,
  } = useFetchAndTransformFilesInApi(reviewId);

  useEffect(() => {
    if (shouldForceRefresh) {
      refresh();
    }
  }, [shouldForceRefresh, refresh]);

  const makeFilePrimary = useCallback(
    async (fileId: string | number) => {
      if (files.length === 0) return;

      await makeFilePrimaryInApi(referenceUrl, fileId);
      await refresh();
    },
    [files.length, referenceUrl, makeFilePrimaryInApi, refresh]
  );

  const deleteFile = useCallback(
    async (file: TransformedFile) => {
      if (files.length === 0) return;

      await deleteFileFromApi(file);
      await refresh();
    },
    [files.length, deleteFileFromApi, refresh]
  );

  return {
    files,
    makeFilePrimary,
    deleteFile,
  };
}
